import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { Alert, AlertIcon, Box, Flex } from '@chakra-ui/react';

import PhoneVerificationModal from 'app/components/Homepage/PhoneVerificationModal/PhoneVerificationModal';
import Navbar from 'app/components/Navbar';
import NavbarActions from 'app/components/Navbar/NavbarActions';

import { unauthenticatedPaths } from 'app/constants/routes';

import { Suspense, useEffect, useMemo } from 'react';

import { Footer } from '@aslanpay/portal-common-lib';
import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import useAuthContext from 'app/hooks/useAuthContext';
import { buildNavSections, useActiveNavId } from 'app/routes/sidebar/navConfig';

import useNavbarActions from 'app/hooks/useNavbarActions';
import useIsPotManager from 'app/hooks/useIsPotManager';
import { canAccessModule } from 'app/utils/moduleAccess';

import { SidebarContext } from 'app/contexts/Sidebar';

import AdminSidebar from './AdminSidebar/AdminSidebar';
import EmployeeSidebar from './EmployeeSidebar/EmployeeSidebar';
import KybInformationHeader from './KybInformationHeader/KybInformationHeader';

const VERSION = import.meta.env.VITE_APPLICATION_VERSION;

function AuthenticatedLayout() {
  const location = useLocation();

  const { hasBeenShown, user, isModalOpen, closeModal, company, openModal } = useNavbarActions();
  const { activeLoginContext } = useAuthContext();
  const isEmployeeContext = activeLoginContext === 'EMPLOYEE';
  const { isPotManager } = useIsPotManager(isEmployeeContext);

  const { data: companyConfig } = useFetchCompanyConfiguration(company?.id || '');

  const isGiftingEnabled = companyConfig?.modulesConfig?.modules?.GIFTING === 'ENABLED';
  const rewardAndRecognitionStatus = companyConfig?.modulesConfig?.modules?.REWARD_AND_RECOGNITION;
  const benefitHostingStatus = companyConfig?.modulesConfig?.modules?.BENEFITS_HOSTING;
  const canAccessRewardAndRecognition = canAccessModule(rewardAndRecognitionStatus, user?.role);
  const canAccessBenefitHosting = canAccessModule(benefitHostingStatus, user?.role);
  const isViewingProvisionedModule =
    user?.role === 'COMPANY_ADMIN' &&
    ((location.pathname.startsWith('/reward-and-recognition') &&
      rewardAndRecognitionStatus === 'DISABLED') ||
      (location.pathname.startsWith('/benefits') && benefitHostingStatus === 'DISABLED'));

  const sections = useMemo(() => {
    return buildNavSections(
      company?.type ?? 'STANDARD',
      isGiftingEnabled,
      canAccessRewardAndRecognition,
      canAccessBenefitHosting,
      isEmployeeContext,
      isPotManager
    );
  }, [
    company?.type,
    isGiftingEnabled,
    canAccessRewardAndRecognition,
    canAccessBenefitHosting,
    isEmployeeContext,
    isPotManager,
  ]);

  const contextSections = useMemo(
    () => ({
      sections,
    }),
    [sections]
  );

  const activeId = useActiveNavId(sections);

  useEffect(() => {
    if (user && !user.phoneNumberVerified && company?.type === 'GIFTING_ONLY' && !hasBeenShown) {
      openModal();
    }
  }, [user, hasBeenShown, company, openModal]);

  if (!user) {
    return <Navigate to={unauthenticatedPaths.authentication} />;
  }

  // Should not render kyb banner on homepage
  const awaitingKyb = company?.status === 'AWAITING_KYB' && location.pathname !== '/';

  return (
    <SidebarContext.Provider value={contextSections}>
      <Flex minH="100vh" direction={{ base: 'column', lg: 'row' }}>
        {isEmployeeContext ? (
          <EmployeeSidebar sections={sections} activeId={activeId} />
        ) : (
          <AdminSidebar
            sections={sections}
            activeId={activeId}
            companyName={company?.name ?? ''}
            navbarContent={<NavbarActions onDarkBackground />}
          />
        )}
        <Box flex="1" overflowY="auto" display="flex" flexDir="column">
          {!isEmployeeContext && <Navbar />}
          {awaitingKyb && <KybInformationHeader />}
          <Flex flex="1">
            <PhoneVerificationModal isOpen={isModalOpen} onClose={closeModal} />
            <Suspense fallback={null}>
              <Flex flex="1" direction="column">
                {isViewingProvisionedModule && (
                  <Alert status="warning">
                    <AlertIcon />
                    This module is provisioned but is not currently available to employees.
                  </Alert>
                )}
                <Outlet />
              </Flex>
            </Suspense>
          </Flex>
          <Footer version={VERSION} />
        </Box>
      </Flex>
    </SidebarContext.Provider>
  );
}

export default AuthenticatedLayout;
